/**
 * Quantity field formatter
 */

import {useLoadBalance} from '@hconnect/tuc-processes/fields/loadBalance/useLoadBalance'
import {usePlusLoadExpected} from '@hconnect/tuc-processes/fields/plusLoad/usePlusLoadExpected'
import {useQuantity} from '@hconnect/tuc-processes/fields/quantity/useQuantity'
import {formatQuantityDisplay} from '../../../shared/order-editor/utils/quantity.utils'
import {useChangeableField} from '@hconnect/tuc-processes/orderManagement/orderChange'

import {getCombinedFieldButtonStateProps} from '../../utils/getFieldButtonStateProps'

import type {FieldFormatter} from './types'
import {createValueArray} from './utils'

export const useQuantityFormatter = (): FieldFormatter => {
  const {quantitySelection, defaultQuantitySelection, defaultQuantity} = useQuantity()
  const {plusLoad, defaultPlusLoad} = usePlusLoadExpected()
  const loadBalance = useLoadBalance()
  const quantityState = useChangeableField({field: 'quantity'})
  const plusLoadState = useChangeableField('plusLoadExpected')
  const maxLoadSizeState = useChangeableField('maxLoadSize')

  return (context) => {
    const {t} = context
    // Each of quantity / plusLoadExpected / maxLoadSize has its own lead-time deadline.
    // Approval must be evaluated per field, otherwise an untouched field that is past its
    // own deadline (typically maxLoadSize) flags an approval for a quantity change that is
    // still auto-confirmable.
    const {isChanged: hasChanges, requiresApproval} = getCombinedFieldButtonStateProps([
      quantityState,
      plusLoadState,
      maxLoadSizeState
    ])

    const formatQuantityValue = (
      qty: number | undefined,
      hasPlusLoad: boolean,
      split?: {firstTruck: number; secondTruck: number}
    ): string | null => {
      if (!qty) return null
      let value = hasPlusLoad
        ? `${qty} M³ + ${t('orderIntake.rmc.orderManagement.plusLoad', 'plus load')}`
        : `${qty} M³`
      if (split) {
        value += ` ${t('orderIntake.rmc.loadBalance.splitLabel', '(split: {{first}}; {{second}})', {
          first: split.firstTruck,
          second: split.secondTruck
        })}`
      }
      return value
    }

    const oldValue =
      defaultQuantitySelection?.type === 'fullLoads'
        ? (() => {
            let value = formatQuantityDisplay(defaultQuantitySelection, t)
            if (defaultPlusLoad) {
              value += ` + ${t('orderIntake.rmc.orderManagement.plusLoad', 'plus load')}`
            }
            return value
          })()
        : formatQuantityValue(defaultQuantity, !!defaultPlusLoad, loadBalance.defaultSplit)

    // For fullLoads mode, split load does not apply
    const newValue =
      quantitySelection.type === 'fullLoads'
        ? (() => {
            let value = formatQuantityDisplay(quantitySelection, t)
            if (plusLoad) {
              value += ` + ${t('orderIntake.rmc.orderManagement.plusLoad', 'plus load')}`
            }
            return value
          })()
        : formatQuantityValue(
            quantitySelection.volume,
            !!plusLoad,
            loadBalance.isEnabled ? loadBalance.split : undefined
          )

    const values = createValueArray(
      oldValue,
      newValue,
      hasChanges,
      t('orderIntake.rmc.orderManagement.notSpecified', 'Not specified')
    )

    return {values, requiresApproval, hasChanges}
  }
}
